import React, { useState } from 'react';
import { Upload, message, Progress } from 'antd';
import { InboxOutlined, FileExcelOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { uploadFile } from '../services/api';
import type { FileInfo } from '../types';

const { Dragger } = Upload;

interface FileUploadProps {
  onUploadSuccess: (fileInfo: FileInfo) => void;
  title?: string;
  subtitle?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onUploadSuccess,
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const draggerProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.csv,.xlsx,.xls,.tsv',
    showUploadList: false,
    customRequest: async ({ file }) => {
      setUploading(true);
      setProgress(0);
      setUploadError(null);

      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      try {
        const result = await uploadFile(file as File);
        clearInterval(interval);
        setProgress(100);
        message.success(`文件 "${(file as File).name}" 上传成功！`);
        setTimeout(() => {
          onUploadSuccess(result);
        }, 500);
      } catch (error: any) {
        clearInterval(interval);
        const errMsg = error?.response?.data?.detail || '上传失败，请检查后端服务是否启动';
        setUploadError(errMsg);
        message.error(errMsg);
      } finally {
        setUploading(false);
      }
    },
    beforeUpload: (file) => {
      const isValid =
        file.type === 'text/csv' ||
        file.type === 'text/tab-separated-values' ||
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.name.endsWith('.csv') ||
        file.name.endsWith('.tsv') ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls');

      if (!isValid) {
        message.error('仅支持 CSV、TSV、XLSX、XLS 格式的文件！');
        return false;
      }

      const isLt25M = file.size / 1024 / 1024 < 25;
      if (!isLt25M) {
        message.error('文件大小不能超过 25MB！');
        return false;
      }
      return true;
    },
  };

  return (<>
    <Dragger {...draggerProps}>
      <div style={{ padding: '32px 20px' }}>
        {uploading ? (
          <div>
            <FileExcelOutlined style={{ fontSize: 36, color: 'var(--primary)', marginBottom: 12 }} />
            <p style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 12 }}>正在上传并分析数据...</p>
            <Progress
              percent={progress}
              strokeColor={{ '0%': '#533afd', '100%': '#6b54fd' }}
              style={{ maxWidth: 280, margin: '0 auto' }}
            />
          </div>
        ) : (
          <div>
            <InboxOutlined style={{ fontSize: 40, color: 'var(--primary)', marginBottom: 12 }} />
            <p style={{ fontSize: 15, color: 'var(--text-primary)', fontWeight: 500, marginBottom: 6 }}>
              点击或拖拽文件到此区域上传
            </p>
            <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              CSV &middot; TSV &middot; XLSX &middot; XLS &middot; 最大 25 MB
            </p>
          </div>
        )}
      </div>
    </Dragger>
    {uploadError && (
      <div style={{
        marginTop: 8, padding: '8px 12px', borderRadius: 4,
        background: 'var(--danger-bg, #fde8ec)',
        border: '1px solid var(--danger-border, #f5b3c1)',
        color: '#8b1029', fontSize: 12, lineHeight: 1.5,
      }}>
        {uploadError}
        <span
          style={{ marginLeft: 8, color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
          onClick={() => setUploadError(null)}
        >
          重试
        </span>
      </div>
    )}
    </>
  );
};

export default FileUpload;
